/**
 * @file api.ts
 * @brief Super Admin → Links & company info: GET / PUT /api/admin/link-settings
 *        (BACKEND B23, ADR-057, 24-10-2026)
 *
 * @details
 * Until B23 is deployed the GET is a 404: the page says so and offers
 * nothing to edit (it never writes these keys through /api/admin/settings,
 * which ignores them). A Save also refreshes the public config this page
 * load uses, so the footer and links follow at once.
 *
 * DEV: `__setLinkSettingsStub` answers both calls in the page (the preview
 * at /__preview/link-settings), so Save there never reaches a backend.
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiClientError, apiGet, apiPut } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import { __resetPublicConfig } from "@/shared/publicConfig/publicConfig";
import { publicConfigKey } from "@/shared/publicConfig/usePublicConfig";
import type { LinkSection } from "./linkSettings";

export interface LinkSettingsBody {
  sections: LinkSection[];
  supportedLanguages?: { code: string; label: string; locale?: string }[];
  changed?: { key: string; from: string | null; to: string | null }[];
}

export type LinkSettingsState =
  | { status: "ok"; data: LinkSettingsBody }
  /** Backend before B23. */
  | { status: "missing" };

interface Stub {
  get: () => Promise<LinkSettingsBody>;
  put: (settings: Record<string, string | null>) => Promise<LinkSettingsBody>;
}
let stub: Stub | null = null;
export function __setLinkSettingsStub(next: Stub | null) {
  if (import.meta.env.DEV) stub = next;
}

export const linkSettingsKey = ["admin", "link-settings"] as const;

function parseBody(raw: unknown): LinkSettingsBody {
  const body = (raw ?? {}) as Partial<LinkSettingsBody>;
  return {
    sections: Array.isArray(body.sections) ? body.sections : [],
    supportedLanguages: Array.isArray(body.supportedLanguages) ? body.supportedLanguages : [],
    changed: Array.isArray(body.changed) ? body.changed : [],
  };
}

export function useLinkSettings() {
  return useQuery<LinkSettingsState>({
    queryKey: linkSettingsKey,
    retry: false,
    queryFn: async () => {
      if (stub) return { status: "ok", data: await stub.get() };
      try {
        return { status: "ok", data: parseBody(await apiGet<unknown>(adminEndpoints.linkSettings)) };
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return { status: "missing" };
        throw err;
      }
    },
  });
}

export function useUpdateLinkSettings() {
  const qc = useQueryClient();
  return useMutation<LinkSettingsBody, Error, Record<string, string | null>>({
    mutationFn: async (settings) =>
      parseBody(stub ? await stub.put(settings) : await apiPut<unknown>(adminEndpoints.linkSettings, { settings })),
    onSuccess: (data) => {
      const next: LinkSettingsState = { status: "ok", data };
      qc.setQueryData<LinkSettingsState>(linkSettingsKey, next);
      // The links in this page load follow the save.
      __resetPublicConfig();
      qc.invalidateQueries({ queryKey: publicConfigKey });
    },
  });
}
